import { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Button,
  Box,
  Container,
} from "@mui/material";
import { DJSimpleCodeBlock, ICodeBlock } from "../../main/components/codeblock";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IProgress,
  ISessionProps,
  OSType,
  SessionStatus,
} from "../../main/components/IProgress";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { Typography } from "@appsflyer/fe-ui-core";
import transition from "../../main/components/transition";
import {
  amplitudeTrack_continue,
  sendGtagOnce,
} from "../../services/utils";

const StartSDKFlutter = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [devKey, setDevKey] = useState();
  const [appId, setAppId] = useState("");
  const [responseListenerRequired] =
    useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (location.state) {
      const state: ISessionProps = location.state as ISessionProps;
      setAppId(state?.appID ?? "");

      const lsDevKey = localStorage.getItem("devKey");
      if (lsDevKey) {
        setDevKey(JSON.parse(lsDevKey));
      }
    }
  }, [location.state]);

  const handleClick = () => {
    const progressArr: IProgress[] = location.state?.progress.slice();
    progressArr[progressArr.length - 1].isListener = responseListenerRequired;

    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      sessionStatus: SessionStatus.ImplementedNotTested,
      currentPage: location.state?.currentPage + 1,
    };

    // send gtag for implemented not test
    sendGtagOnce(
      "implemented_not_tested",
      "StartSDKFlutter",
      location.state.sessionId
    );

    amplitudeTrack_continue(location.state, { responseListenerRequired });

    navigate(`/startsdk/${location.state.os.toLowerCase()}/testsdktype`, {
      state: progressState,
    });
  };

  const isAndroid = location.state?.os === OSType.FlutterAndroid;
  const isIOS = location.state?.os === OSType.FlutterIOS;
  const appIdNumeric = appId?.startsWith('id') ? appId.substring(2) : appId;
  const codeStep0Android = `...
android {
  ...
  defaultConfig {
    applicationId "${appId}"
    minSdkVersion flutter.minSdkVersion
    ...
  }
  ...
}`;
  const codeStep1 = `import 'package:appsflyer_sdk/appsflyer_sdk.dart';`;
  const codeStep2 = `AppsFlyerOptions appsFlyerOptions = AppsFlyerOptions(
  afDevKey: "${devKey}",${isIOS ? `\n  appId: "${appIdNumeric}",` : ''}
  showDebug: true);

AppsflyerSdk appsflyerSdk = AppsflyerSdk(appsFlyerOptions);

appsflyerSdk.initSdk(
  registerConversionDataCallback: true,
  registerOnAppOpenAttributionCallback: true,
  registerOnDeepLinkingCallback: true
);`;

  const codeBlockArray: ICodeBlock[] = [
    {
      codePrimary: codeStep0Android,
      showLineNumbers: true,
      language: "Groovy DSL",
      highlight: "2,4-5,8,10"
    },
    {
      codePrimary: codeStep1,
      showLineNumbers: true,
    }, {
      codePrimary: codeStep2,
      showLineNumbers: true,
      language: "dart",
    }
  ];

  const steps = [
    ...(isAndroid ? [{
      label: t("StartSDK_StartSDK_android_flutter_step0_label"),
      component: (
        <>
          <Typography mb={1.5}>
            <Trans
              i18nKey="StartSDK_StartSDK_android_flutter_step0_description"
              components={{ code: <code /> }}
            />
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[0]}></DJSimpleCodeBlock>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    }] : []),
    {
      label: t("ip_SendInApp_flutter_importAppsFlyerSDK"),
      component: (
        <>
          
          <DJSimpleCodeBlock {...codeBlockArray[1]}></DJSimpleCodeBlock>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("StartSDK_StartSDK_flutter_step2_label"),
      component: (
        <>
          <DJSimpleCodeBlock {...codeBlockArray[2]}></DJSimpleCodeBlock>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    }, 
    
  ];

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} style={{ minHeight: "50vh" }}>
        <Typography variant="h1">
          {t("startSDK_StartSDK_flutter_StartSDK")}
        </Typography>
        <Stepper
          activeStep={activeStep}
          style={{ border: "none" }}
          orientation="vertical"
        >
          {steps.map(({ label, component, shouldDisplayNavigationButtons }) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {component}
                {shouldDisplayNavigationButtons && (
                  <Box sx={{ mt: 2 }}>
                    {activeStep !== 0 && (
                      <Button
                        variant="text"
                        color="primary"
                        onClick={handleBack}
                        // sx={{ mt: 2, mr: 1 }}
                      >
                        {t("general_Back")}
                      </Button>
                    )}
                    {activeStep !== steps.length - 1 && (
                      <Button
                        // sx={{ mt: 2 }}
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                      >
                        {activeStep === steps.length - 1
                          ? t("general_Finish")
                          : t("general_Next")}
                      </Button>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
      >
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => {
            const progressState: ISessionProps = {
              ...location.state,
              currentPage: location.state?.currentPage - 1,
            };
            navigate(`/startsdk/${location.state.os.toLowerCase()}/installsdk`, {
              state: progressState,
              replace: true,
            });
          }}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          onClick={() => handleClick()}
        >
          {t("general_Continue")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(StartSDKFlutter);
